import type { CSSProperties } from "react";
import { Badge, Button, EmptyState, Kicker, Panel, Status, Table, Title } from "@fwf/fwfa-design-system";

import { applyFilters, toFilterable } from "../filters";
import type { FilterState } from "../filters";
import { formatDate, formatRelative, titleCase } from "../format";
import type { Actions, AdminData, Toasts } from "../hooks";
import { spriteState } from "../state";
import { stateTone } from "../types";

export type SectionProps = {
  data: AdminData;
  filters: FilterState;
  actions: Actions;
  toasts: Toasts;
};

// The DS wires its mono face through `td.fwfa-table-mono`, which would drag a
// cell's sub-line into mono along with the id. Reading the token directly keeps
// the identifier mono and leaves the sub-line in body type.
const MONO: CSSProperties = { fontFamily: "var(--fwfa-font-mono)" };

export function SpritesSection({ data, filters, actions }: SectionProps) {
  const overview = data.overview;
  // Sprites is one provider among several now, so the section is absent rather
  // than empty when the deployment is not running on it.
  if (!overview || !overview.spritesEnabled) return null;

  const sprites = applyFilters(overview.sprites, filters, (sprite) =>
    toFilterable(spriteState(sprite), "n/a", [
      sprite.sandboxId,
      sprite.shortSandboxId,
      sprite.ownerUserId,
      sprite.status,
    ]),
  );

  const total = overview.sprites.length;

  return (
    <section className="fwfa-section">
      <div className="fwfa-section-header">
        <div>
          <Kicker>Workspace provider</Kicker>
          <Title as="h2" className="fwfa-title--section">
            Sprites
          </Title>
        </div>
        <p className="fwfa-type-label">
          {sprites.length === total ? `${total} discovered` : `${sprites.length} of ${total} discovered`}
          {" · managed and orphaned, by token"}
        </p>
      </div>

      <Panel>
        {sprites.length === 0 ? (
          total === 0 ? (
            <EmptyState title="No sprites found">
              The provider token can see no workspaces. Anything created here will show up on the
              next refresh.
            </EmptyState>
          ) : (
            <EmptyState title="No sprites match these filters">
              {total === 1 ? "The one sprite" : `All ${total} sprites`} are hidden by the current
              state, health, or search filters.
            </EmptyState>
          )
        ) : (
          <Table reflow>
            <thead>
              <tr>
                <th>Workspace</th>
                <th>State</th>
                <th>Owner</th>
                <th>Managed</th>
                <th>Created / Last used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sprites.map((sprite) => {
                const state = spriteState(sprite);
                const deleteKey = `sprite:${sprite.sandboxId}:delete`;
                const deleting = actions.isPending(deleteKey);
                return (
                  <tr key={sprite.sandboxId}>
                    <td data-label="Workspace">
                      <div>
                        <span style={MONO}>{sprite.sandboxId}</span>
                        <p className="fwfa-type-label">
                          {sprite.shortSandboxId} · created {formatDate(sprite.createdAt)}
                        </p>
                      </div>
                    </td>
                    <td data-label="State">
                      {/* Normalised state drives the tone and the live dot; the label keeps
                          the provider's own word so it still matches the Sprites console. */}
                      <Status tone={stateTone(state)} live={state === "running"}>
                        {titleCase(sprite.status)}
                      </Status>
                    </td>
                    <td data-label="Owner">
                      <div>
                        <span>{sprite.ownerUserId ?? "—"}</span>
                        <p className="fwfa-type-label">{sprite.ownerSource}</p>
                      </div>
                    </td>
                    <td data-label="Managed">
                      {sprite.managed ? (
                        <Badge variant="soft">Managed</Badge>
                      ) : (
                        <Badge>Unmanaged</Badge>
                      )}
                    </td>
                    <td data-label="Created / Last used">
                      <div>
                        <span>{formatDate(sprite.createdAt)}</span>
                        <p className="fwfa-type-label">used {formatRelative(sprite.lastUsed)}</p>
                      </div>
                    </td>
                    <td data-label="Actions">
                      <Button
                        variant="secondary"
                        compact
                        loading={deleting}
                        disabled={deleting}
                        onClick={() =>
                          void actions.run({
                            key: deleteKey,
                            url: "/api/admin/sprites/delete",
                            body: { sandboxId: sprite.sandboxId },
                            confirm: {
                              title: "Delete this sprite?",
                              body: `${sprite.sandboxId} will be removed. This cannot be undone.`,
                              confirmLabel: "Delete sprite",
                              danger: true,
                            },
                            successTitle: "Sprite deleted",
                            successMessage: sprite.sandboxId,
                            failureTitle: "Could not delete the sprite",
                          })
                        }
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Panel>
    </section>
  );
}
